import axios from 'axios'
import router from '../router/index'
import store from '../state/store'  // Import the Vuex store
import supabase from './supabaseClient'
import { wrapApiError } from '@/utils/apiError'
import { REFERRAL_COOKIE, PROMO_COOKIE, getCookie } from '@/utils/cookies'
import AnalyticsService from '@/services/analytics'

const API_URL = process.env.VUE_APP_API_URL || 'http://localhost:8000'
const RECENT_REFRESH_WINDOW = 60 * 1000; // 60 seconds – prevent duplicate refreshes across tabs

class AuthBackend {
    constructor(serverConfig) {
        if (serverConfig) {
            // Reserved for future Firebase integration
        }
    }

    /**
     * Registers the user with given details
     */
    registerUser(firstName, lastName, email, password, phone = null, planId = null, referralCode = null, promoCode = null) {
        return new Promise((resolve, reject) => {
            const payload = {
                first_name: firstName,
                last_name: lastName,
                email,
                password
            };

            // Add phone if provided
            if (phone) {
                payload.phone = phone;
            }

            // Add plan_id if provided
            if (planId) {
                payload.plan_id = planId;
            }

            // Add referral_code if provided
            if (referralCode) {
                payload.referral_code = referralCode;
            }

            // Add promo_code if provided (billing coupon, same rails as referral)
            if (promoCode) {
                payload.promo_code = promoCode;
            }

            axios.post(`${API_URL}/auth/register`, payload, {
                skipAuthRefresh: true // Skip interceptor for this call
            })
            .then((response) => {
                resolve(response.data);
            })
            .catch((error) => {
                reject(wrapApiError(error, 'Registration failed'));
            });
        });
    }

    /**
     * Login user with given details
     */
    loginUser(email, password) {
        return new Promise((resolve, reject) => {
            axios.post(`${API_URL}/auth/login`, {
                email,
                password
            }, {
                skipAuthRefresh: true // Skip interceptor for this call
            }).then((response) => {
                resolve(response.data);
            }).catch((error) => {
                reject(wrapApiError(error, 'Login failed'));
            });
        });
    }

    /**
     * Login with access token (used for email confirmation)
     */
    loginWithToken(access_token) {
        return new Promise((resolve, reject) => {
            axios.get(`${API_URL}/auth/login-with-token`, {
                params: {
                    token: access_token
                },
                skipAuthRefresh: true // Skip interceptor for this call
            }).then((response) => {
                resolve(response.data);
            }).catch((error) => {
                reject(wrapApiError(error, 'Failed to confirm the account'));
            });
        });
    }

    /**
     * Login with hash token (used for magic links)
     */
    verifyLoginHash(token_hash) {
        return new Promise((resolve, reject) => {
            axios.get(`${API_URL}/auth/admin/login_user/verify`, {
                params: {
                    token_hash
                },
                skipAuthRefresh: true // Skip interceptor for this call
            }).then((response) => {
                resolve(response.data);
            }).catch((error) => {
                reject(wrapApiError(error, 'Invalid or expired login link'));
            });
        });
    }

    /**
     * Get Google OAuth URL
     * @param {string} redirectTo - Optional custom redirect URL
     */
    getGoogleAuthUrl(redirectTo = null) {
        return new Promise((resolve, reject) => {
            // Use Supabase client-side auth instead of server API
            // Convert Vue route path to full URL if it's a relative path
            const redirectUrl = redirectTo ? 
                (redirectTo.startsWith('http') ? redirectTo : window.location.origin + redirectTo) : 
                window.location.origin;

            console.log("getGoogleAuthUrl::Redirect URL", redirectUrl);
                
            supabase.auth.signInWithOAuth({
                provider: 'google',
                options: {
                    redirectTo: redirectUrl,
                    queryParams: {
                        access_type: 'offline',
                        prompt: 'consent',
                    }
                }
            })
            .then((response) => {
                if (response.error) {
                    reject({ message: response.error.message || 'Failed to get Google authentication URL' });
                    return;
                }
                resolve({ url: response.data.url });
            })
            .catch(error => {
                reject({ message: error.message || 'Failed to get Google authentication URL' });
            });
        });
    }

    /**
     * Report a client-side OAuth login to the backend, and report a signup.
     *
     * The OAuth handshake never touches a backend login endpoint, so without
     * this call the login would leave no AuthEvent (IP/UA access record) — and,
     * until this carried analytics, no conversion either: signing up with
     * Google was invisible to every advertising platform, while Google is how
     * people actually arrive.
     *
     * Never allowed to break the login flow: a failure is logged and swallowed.
     */
    recordOauthLogin(headers, isNewUser) {
        return axios.post(`${API_URL}/auth/oauth-complete`, {
            provider: 'google',
            is_new_user: isNewUser
        }, { headers, skipAuthRefresh: true })
            .then((response) => {
                // The server decides whether this sign-in really was a signup —
                // the browser's own claim is not evidence — reports it, and
                // hands back the id it used. Reusing that id is what stops one
                // signup being counted as two conversions. It is absent for
                // every sign-in that was not a signup.
                const registrationEventId = response?.data?.analytics?.registration_event_id;
                if (registrationEventId) {
                    AnalyticsService.trackEvent('CompleteRegistration', {
                        content_name: 'new_user',
                        status: 'success',
                        method: 'google'
                    }, registrationEventId);
                }
            })
            .catch((error) => {
                console.warn('Failed to record OAuth login event:', error?.message);
            });
    }

    /**
     * Handle Google OAuth callback
     */
    handleGoogleCallback() {
        return new Promise((resolve, reject) => {
            supabase.auth.getSession()
                .then((response) => {
                    if (response.error) {
                        reject({ message: response.error.message || 'Failed to get session' });
                        return;
                    }
                    
                    if (!response.data.session) {
                        reject({ message: 'No session found after Google login' });
                        return;
                    }

                    // Convert Supabase session format to our app's format
                    const sessionData = {
                        access_token: response.data.session.access_token,
                        refresh_token: response.data.session.refresh_token,
                        expires_at: response.data.session.expires_at
                    };

                    // First, store the session
                    const session = { session: sessionData };

                    // Now try to fetch the user profile from our backend
                    // Use the Supabase token for authentication
                    const headers = { Authorization: `Bearer ${sessionData.access_token}` };
                    
                    axios.get(`${API_URL}/profile`, { headers, skipAuthRefresh: true })
                        .then(profileResponse => {
                            // Profile exists, resolve with both session and profile
                            session.profile = profileResponse.data;
                            this.recordOauthLogin(headers, false);
                            resolve(session);
                        })
                        .catch(error => {
                            // If 404 or 401, profile doesn't exist yet - create one using Supabase user data
                            if (error.response && (error.response.status === 404 || error.response.status === 401)) {
                                // Get user data from Supabase
                                supabase.auth.getUser()
                                    .then(userResponse => {
                                        if (userResponse.error) {
                                            reject({ message: userResponse.error.message || 'Failed to get user data' });
                                            return;
                                        }

                                        const userData = userResponse.data.user;
                                        
                                        // Create a new profile with data from Supabase
                                        const profileData = {
                                            email: userData.email,
                                            first_name: userData.user_metadata?.first_name || userData.email.split('@')[0],
                                            last_name: userData.user_metadata?.last_name || '',
                                            completed_onboarding: false // New users need to complete onboarding
                                        };

                                        // Include referral code from cookie if present
                                        const referralCode = getCookie(REFERRAL_COOKIE);
                                        if (referralCode) {
                                            profileData.referral_code = referralCode;
                                        }

                                        // Include promo (billing coupon) code from cookie if present
                                        const promoCode = getCookie(PROMO_COOKIE);
                                        if (promoCode) {
                                            profileData.promo_code = promoCode;
                                        }
                                        
                                        // Create profile using the API
                                        axios.post(`${API_URL}/profile`, profileData, {
                                            headers,
                                            skipAuthRefresh: true
                                        })
                                            .then(createResponse => {
                                                session.profile = createResponse.data;
                                                this.recordOauthLogin(headers, true);
                                                resolve(session);
                                            })
                                            .catch(createError => {
                                                console.error('Failed to create profile:', createError);

                                                // Don't use a fake profile - properly reject to show the error
                                                // This ensures the user sees the actual error and we can debug it
                                                reject(wrapApiError(createError, 'Failed to create user profile'));
                                            });
                                    })
                                    .catch(userError => {
                                        console.error('Failed to get user data from Supabase:', userError);
                                        reject({ message: userError.message || 'Failed to get user data' });
                                    });
                            } else {
                                // For other errors, reject with the error message
                                console.error('Failed to fetch profile:', error);
                                reject(wrapApiError(error, 'Failed to load the user profile'));
                            }
                        });
                })
                .catch(error => {
                    reject({ message: error.message || 'Failed to handle Google callback' });
                });
        });
    }

    /**
     * Send password reset email.
     * Rejects with the wrapApiError shape so callers can translate the
     * backend's {code, params, message} payload via translateApiError.
     */
    forgetPassword(email) {
        return new Promise((resolve, reject) => {
            axios.post(`${API_URL}/auth/reset-password`, {
                email
            }, {
                skipAuthRefresh: true
            }).then((response) => {
                resolve(response.data);
            }).catch((error) => {
                reject(wrapApiError(error, 'Failed to send the reset email'));
            });
        });
    }

    /**
     * Confirm password reset with token and new password.
     * Rejects with the wrapApiError shape — the backend distinguishes an
     * expired link from a reused password here, and both must survive to the UI.
     */
    confirmPasswordReset(token, password) {
        return new Promise((resolve, reject) => {
            axios.post(`${API_URL}/auth/confirm-reset-password`, {
                token,
                password
            }, {
                skipAuthRefresh: true
            }).then((response) => {
                resolve(response.data);
            }).catch((error) => {
                reject(wrapApiError(error, 'Failed to reset the password'));
            });
        });
    }

    /**
     * Logout user
     */
    logout() {
        return new Promise((resolve, reject) => {
            const refreshToken = this.getRefreshToken();
            
            // Call server logout if we have a refresh token
            if (refreshToken) {
                axios.post(`${API_URL}/auth/logout`, {
                    refresh_token: refreshToken
                }, {
                    skipAuthRefresh: true
                }).then(() => {
                    resolve(true);
                }).catch((error) => {
                    // Even if server logout fails, we should clear local data
                    console.warn('Server logout failed:', error);
                    resolve(true);
                });
            } else {
                resolve(true);
            }
            
            // Sign out from Supabase as well
            supabase.auth.signOut().catch(console.warn);
        });
    }

    /**
     * Update user profile
     */
    updateProfile(profileData) {
        return new Promise((resolve, reject) => {
            axios.put(`${API_URL}/profile`, profileData).then((response) => {
                resolve(response.data);
            }).catch((error) => {
                reject(wrapApiError(error, 'Failed to update the profile'));
            });
        });
    }

    /**
     * Update password
     */
    updatePassword(passwordData) {
        return new Promise((resolve, reject) => {
            axios.put(`${API_URL}/auth/password`, passwordData)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(wrapApiError(error, 'Failed to update the password'));
                });
        });
    }

    /**
     * Get user profile
     */
    getProfile() {
        return new Promise((resolve, reject) => {
            // Check if we have a valid token before making the request
            const token = this.getAuthToken();
            const options = {};
            
            if (!token) {
                console.warn('getProfile called without a valid token');
                options.skipAuthRefresh = true; // Skip token refresh if no token
            }
            
            axios.get(`${API_URL}/profile`, options)
                .then((response) => {
                    resolve(response.data);
                })
                .catch((error) => {
                    reject(wrapApiError(error, 'Failed to load the profile'));
                });
        });
    }

    /**
     * Get auth token from store
     */
    getAuthToken() {
        const authUser = store.state.auth.authUser;
        return authUser?.access_token || null;
    }

    /**
     * Returns the authenticated user
     */
    getAuthenticatedUser() {
        return store.state.auth.authUser;
    }

    /**
     * Get refresh token from store
     */
    getRefreshToken() {
        const authUser = store.state.auth.authUser;
        return authUser?.refresh_token || null;
    }

    /**
     * Refresh the access token
     */
    refreshToken() {
        return new Promise((resolve, reject) => {
            // A tab waking from freeze/sleep may hold a rotated-out token in
            // memory while another tab already advanced the session in
            // localStorage. Sync first and refresh with the newest token —
            // replaying an old one trips Supabase reuse detection and revokes
            // the entire token family (forced logout everywhere).
            store.dispatch('auth/syncFromLocalStorage').catch(() => null).then(() => {
                const refresh_token = this.getRefreshToken();
                if (!refresh_token) {
                    reject(new Error('No refresh token available'));
                    return;
                }

                // Check if a refresh is already in progress (global state)
                if (store.state.auth.refreshInProgress) {
                    console.log('Token refresh already in progress, waiting...');
                    // Poll for completion instead of immediately starting another refresh
                    let pollCount = 0;
                    const maxPolls = 50; // 5 seconds max wait
                    const pollInterval = setInterval(() => {
                        pollCount++;
                        if (!store.state.auth.refreshInProgress) {
                            clearInterval(pollInterval);
                            const authUser = store.state.auth.authUser;
                            if (authUser?.access_token) {
                                console.log('Using token from completed refresh');
                                resolve(authUser);
                            } else {
                                reject(new Error('Token refresh completed but no valid token found'));
                            }
                            return;
                        }

                        if (pollCount >= maxPolls) {
                            clearInterval(pollInterval);
                            // A refresh that never completed (e.g. tab slept
                            // mid-request) must not wedge this tab forever —
                            // clear the flag so the next attempt can proceed.
                            store.commit('auth/SET_REFRESH_IN_PROGRESS', false);
                            reject(new Error('Token refresh timeout'));
                        }
                    }, 100);
                    return;
                }

                // If another tab refreshed moments ago, the just-synced session
                // is current — reuse it instead of rotating the token again.
                const lastRefresh = parseInt(localStorage.getItem('auth_refresh_timestamp') || '0');
                if (lastRefresh && Date.now() - lastRefresh < RECENT_REFRESH_WINDOW) {
                    const authUser = store.state.auth.authUser;
                    if (authUser?.access_token && !this.isTokenExpired()) {
                        console.log('Token recently refreshed by another tab, reusing it');
                        resolve(authUser);
                        return;
                    }
                }

                this._performTokenRefresh(refresh_token, resolve, reject);
            });
        });
    }

    /**
     * Internal method to perform token refresh
     */
    _performTokenRefresh(refresh_token, resolve, reject) {
        // Set refresh in progress state
        store.commit('auth/SET_REFRESH_IN_PROGRESS', true);
        
        console.log('Starting token refresh...');
        
        axios.post(`${API_URL}/auth/refresh`, { refresh_token }, {
            skipAuthRefresh: true, // Skip interceptor for this call
            timeout: 10000 // 10 second timeout
        })
        .then((response) => {
            const authData = response.data;
            console.log('Token refresh successful');
            
            // Store new auth data
            store.commit('auth/SET_AUTH_USER', authData);
            store.commit('auth/SET_REFRESH_TIMESTAMP', Date.now());
            
            // Update localStorage immediately for cross-tab coordination
            localStorage.setItem('auth_refresh_timestamp', Date.now().toString());
            
            resolve(authData);
        })
        .catch((error) => {
            console.error('Token refresh failed:', error);
            
            // Check if it's a network error vs auth error
            if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                console.error('Token refresh timeout');
                reject(new Error('Token refresh timeout - please try again'));
            } else if (error.response?.status === 401) {
                console.error('Refresh token is invalid or expired');
                // Clear authentication state
                store.commit('auth/SET_AUTH_USER', null);
                store.commit('auth/SET_CURRENT_USER', null);
                store.commit('auth/SET_REFRESH_TIMESTAMP', 0);
                
                // Redirect to login with current path as redirect parameter
                const redirectFrom = window.location.pathname;
                router.push({ path: '/app/login', query: { redirectFrom } });
                
                reject(new Error('Session expired - please log in again'));
            } else {
                // Other errors (500, network issues, etc.)
                reject(new Error(`Token refresh failed: ${error.message}`));
            }
        })
        .finally(() => {
            // Always clear the refresh in progress state
            store.commit('auth/SET_REFRESH_IN_PROGRESS', false);
        });
    }

    /**
     * Check if token is expired or will expire soon
     */
    isTokenExpired() {
        const authUser = store.state.auth.authUser;
        if (!authUser || !authUser.access_token || !authUser.expires_at) {
            console.log('Auth check: No valid token or expiration found');
            return false; // Changed from true to false - no token means no need to refresh
        }
        
        // Check if token expires in the next 5 minutes (300 seconds)
        const expirationTime = authUser.expires_at * 1000; // Convert to milliseconds
        const currentTime = Date.now();
        const timeBuffer = 5 * 60 * 1000; // 5 minutes in milliseconds
        
        const isExpired = currentTime >= (expirationTime - timeBuffer);
        const timeToExpiry = Math.floor((expirationTime - currentTime) / 1000);
        
        return isExpired;
    }
}

let _authBackend = null;

const setupAxiosInterceptors = () => {
    let isRefreshing = false;
    let failedQueue = [];

    const processQueue = (error, token = null) => {
        failedQueue.forEach(prom => {
            if (error) {
                prom.reject(error);
            } else {
                prom.resolve(token);
            }
        });
        failedQueue = [];
    };

    // Request interceptor
    axios.interceptors.request.use(
        async (config) => {
            // Skip auth for certain requests
            if (config.skipAuthRefresh) {
                return config;
            }

            // If a refresh is in progress, wait for it to complete
            if (isRefreshing) {
                try {
                    await new Promise((resolve, reject) => {
                        failedQueue.push({ resolve, reject });
                    });
                } catch (error) {
                    console.warn('Token refresh failed during request queue:', error);
                }
            }

            // Skip token refresh for registration and login endpoints
            const isAuthEndpoint = config.url.includes('/auth/register') || 
                                  config.url.includes('/auth/login');
            
            // Proactively refresh if token is (about to be) expired and not an auth endpoint
            if (_authBackend && !isAuthEndpoint && _authBackend.isTokenExpired()) {
                try {
                    console.log('Token expired/expiring, refreshing before request...');
                    await _authBackend.refreshToken();
                } catch (e) {
                    console.error('Proactive token refresh failed:', e);
                    // Continue with request - the response interceptor will handle 401s
                }
            }

            // Get the most up-to-date token from the store
            const token = _authBackend?.getAuthToken();
            if (token) {
                config.headers.Authorization = `Bearer ${token}`;
            }

            return config;
        },
        (error) => {
            return Promise.reject(error);
        }
    );

    // Response interceptor
    axios.interceptors.response.use(
        (response) => response,
        async (error) => {
            const originalRequest = error.config;

            // Skip refresh logic for certain conditions
            if (
                error.response?.status !== 401 || 
                originalRequest._retry || 
                originalRequest.skipAuthRefresh ||
                !_authBackend
            ) {
                return Promise.reject(error);
            }

            // Check if we have a refresh token
            const refreshToken = _authBackend.getRefreshToken();
            if (!refreshToken) {
                // No refresh token available, log out user
                console.warn('No refresh token available, logging out user');
                store.commit('auth/SET_AUTH_USER', null);
                store.commit('auth/SET_CURRENT_USER', null);
                router.push('/app/login');
                return Promise.reject(error);
            }

            // Mark this request as retried
            originalRequest._retry = true;

            // If already refreshing, queue this request
            if (isRefreshing) {
                return new Promise((resolve, reject) => {
                    failedQueue.push({ 
                        resolve: (token) => {
                            originalRequest.headers.Authorization = `Bearer ${token}`;
                            resolve(axios(originalRequest));
                        }, 
                        reject 
                    });
                })
                .catch(err => Promise.reject(err));
            }

            // Start refresh process
            isRefreshing = true;
            store.commit('auth/SET_REFRESH_IN_PROGRESS', true);

            try {
                console.log('Starting token refresh due to 401 error...');
                const response = await _authBackend.refreshToken();
                const newToken = response.access_token;
                
                console.log('Token refresh successful, retrying original request');
                
                // Update the original request with new token
                originalRequest.headers.Authorization = `Bearer ${newToken}`;
                
                // Process queued requests
                processQueue(null, newToken);
                
                // Retry the original request
                return axios(originalRequest);
            } catch (refreshError) {
                console.error('Token refresh failed:', refreshError);
                
                // Refresh failed, process queue with error
                processQueue(refreshError, null);
                
                // Clear auth state and redirect to login
                store.commit('auth/SET_AUTH_USER', null);
                store.commit('auth/SET_CURRENT_USER', null);
                router.push('/app/login');
                
                return Promise.reject(refreshError);
            } finally {
                isRefreshing = false;
                store.commit('auth/SET_REFRESH_IN_PROGRESS', false);
            }
        }
    );
};

/**
 * Initialize the backend
 */
const initAuthBackend = (config) => {
    if (!_authBackend) {
        _authBackend = new AuthBackend(config);
        setupAxiosInterceptors();
    }
    return _authBackend;
};

/**
 * Returns the auth backend instance
 */
const getAuthBackend = () => {
    return _authBackend;
};

export { initAuthBackend, getAuthBackend }; 